import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Car, CheckCircle2, MessageCircle } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialForm = { nome: "", email: "", telefono: "", arrivo: "", partenza: "", messaggio: "", azienda: "", privacy: false };

export default function Contatti() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const { l, data, t } = useLang();
  const { SITE } = data;
  const c = t.contact;

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const validate = () => {
    const errs = {};
    if (!form.nome.trim()) errs.nome = c.form.errName;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = c.form.errEmail;
    if (form.messaggio.trim().length < 10) errs.messaggio = c.form.errMessage;
    if (!form.privacy) errs.privacy = c.form.errPrivacy;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        nome: form.nome.trim(),
        email: form.email.trim(),
        telefono: form.telefono.trim(),
        arrivo: form.arrivo,
        partenza: form.partenza,
        messaggio: form.messaggio.trim(),
        azienda: form.azienda,
      });
      setSent(true);
      setForm(initialForm);
      toast.success(c.form.toastOk);
    } catch {
      toast.error(c.form.toastErr + SITE.phone);
    } finally {
      setSending(false);
    }
  };

  const inputCls = (err) =>
    `w-full bg-card border ${err ? "border-red-500" : "border-sand"} px-4 py-3.5 text-sm text-ink placeholder:text-body/60 focus:border-sage focus:outline-none transition-colors duration-300`;

  return (
    <>
      <Seo title={c.seo.title} description={c.seo.description} path="/contatti" />
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-14">
        <Reveal>
          <p className="eyebrow mb-5">{c.eyebrow}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-ink max-w-3xl">
            {c.h1}
          </h1>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-10">
          <Reveal>
            <ul className="space-y-6">
              <li className="flex gap-4">
                <MapPin className="w-5 h-5 text-terra mt-1 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-body">{c.labels.address}</p>
                  <p className="mt-1 font-serif text-xl text-ink">{SITE.name}</p>
                  <p className="text-sm text-body">{SITE.address} · {SITE.cap} {SITE.city}</p>
                </div>
              </li>
              <li className="flex gap-4">
                <Phone className="w-5 h-5 text-terra mt-1 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-body">{c.labels.phone}</p>
                  <a href={SITE.phoneHref} data-testid="contact-phone-link" className="mt-1 block font-serif text-xl text-ink hover:text-terra transition-colors duration-300">
                    {SITE.phone}
                  </a>
                </div>
              </li>
              <li className="flex gap-4">
                <Mail className="w-5 h-5 text-terra mt-1 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-body">{c.labels.email}</p>
                  <a href={SITE.emailHref} data-testid="contact-email-link" className="mt-1 block font-serif text-xl text-ink hover:text-terra transition-colors duration-300 break-all">
                    {SITE.email}
                  </a>
                </div>
              </li>
              <li className="flex gap-4">
                <MessageCircle className="w-5 h-5 text-terra mt-1 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-body">{c.labels.whatsapp}</p>
                  <a href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="mt-1 block font-serif text-xl text-ink hover:text-terra transition-colors duration-300">
                    {SITE.whatsapp}
                  </a>
                  <p className="mt-1 text-xs text-body">{c.labels.whatsappNote}</p>
                </div>
              </li>
              <li className="flex gap-4">
                <Clock className="w-5 h-5 text-terra mt-1 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-body">{c.labels.hours}</p>
                  <p className="mt-1 text-sm text-body leading-relaxed">{c.labels.hoursText}</p>
                </div>
              </li>
              <li className="flex gap-4">
                <Car className="w-5 h-5 text-terra mt-1 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-body">{c.labels.arrival}</p>
                  <p className="mt-1 text-sm text-body leading-relaxed">{c.labels.arrivalText}</p>
                  <a
                    href={SITE.mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-directions-link"
                    className="mt-3 inline-flex items-center gap-2 text-[13px] font-semibold text-sage hover:text-terra transition-colors duration-300"
                  >
                    {c.labels.directions}
                  </a>
                </div>
              </li>
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            {mapLoaded ? (
              <div className="img-frame aspect-[4/3]">
                <iframe
                  title={c.map.title}
                  src={SITE.mapsEmbed}
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setMapLoaded(true)}
                data-testid="load-map-button"
                className="w-full aspect-[4/3] border border-sand bg-surface flex flex-col items-center justify-center gap-3 text-sage hover:bg-sand/40 transition-colors duration-300"
              >
                <MapPin className="w-7 h-7" aria-hidden="true" />
                <span className="text-sm font-semibold">{c.map.load}</span>
                <span className="text-xs text-body max-w-xs px-6">{c.map.consent}</span>
              </button>
            )}
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="border border-sand bg-card p-7 md:p-10">
            {sent ? (
              <div data-testid="contact-form-success" className="text-center py-10">
                <CheckCircle2 className="w-10 h-10 text-sage mx-auto" aria-hidden="true" />
                <h2 className="font-serif text-3xl text-ink mt-5">{c.form.successTitle}</h2>
                <p className="mt-4 text-sm md:text-base text-body leading-relaxed max-w-md mx-auto">
                  {c.form.successTextPre}{SITE.phone}.
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  data-testid="contact-form-new-request-button"
                  className="mt-8 inline-flex items-center rounded-full border border-sage px-6 py-3 text-sm font-medium text-sage hover:bg-sage hover:text-cream transition-colors duration-300"
                >
                  {c.form.newRequest}
                </button>
              </div>
            ) : (
              <>
                <h2 className="font-serif text-2xl md:text-3xl text-ink">{c.form.title}</h2>
                <p className="mt-3 text-sm text-body leading-relaxed">
                  {c.form.intro}
                  <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" className="text-sage font-semibold hover:text-terra transition-colors duration-300">
                    {c.form.introLink}
                  </a>.
                </p>
                <form onSubmit={submit} noValidate data-testid="contact-form" className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="azienda">{c.form.company}</label>
                    <input id="azienda" name="azienda" type="text" tabIndex={-1} autoComplete="off" value={form.azienda} onChange={set("azienda")} />
                  </div>
                  <div>
                    <label htmlFor="nome" className="block text-xs uppercase tracking-[0.16em] text-body mb-2">{c.form.name}</label>
                    <input id="nome" data-testid="contact-name-input" type="text" value={form.nome} onChange={set("nome")} className={inputCls(errors.nome)} autoComplete="name" />
                    {errors.nome && <p className="mt-1.5 text-xs text-red-600">{errors.nome}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs uppercase tracking-[0.16em] text-body mb-2">{c.form.email}</label>
                    <input id="email" data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls(errors.email)} autoComplete="email" />
                    {errors.email && <p className="mt-1.5 text-xs text-red-600">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="telefono" className="block text-xs uppercase tracking-[0.16em] text-body mb-2">{c.form.phone}</label>
                    <input id="telefono" data-testid="contact-phone-input" type="tel" value={form.telefono} onChange={set("telefono")} className={inputCls(false)} autoComplete="tel" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="arrivo" className="block text-xs uppercase tracking-[0.16em] text-body mb-2">{c.form.arrival}</label>
                      <input id="arrivo" data-testid="contact-arrival-input" type="date" value={form.arrivo} onChange={set("arrivo")} className={inputCls(false)} />
                    </div>
                    <div>
                      <label htmlFor="partenza" className="block text-xs uppercase tracking-[0.16em] text-body mb-2">{c.form.departure}</label>
                      <input id="partenza" data-testid="contact-departure-input" type="date" value={form.partenza} onChange={set("partenza")} className={inputCls(false)} />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="messaggio" className="block text-xs uppercase tracking-[0.16em] text-body mb-2">{c.form.message}</label>
                    <textarea id="messaggio" data-testid="contact-message-input" rows={5} value={form.messaggio} onChange={set("messaggio")} className={`${inputCls(errors.messaggio)} resize-y`} />
                    {errors.messaggio && <p className="mt-1.5 text-xs text-red-600">{errors.messaggio}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="flex items-start gap-3 text-xs text-body leading-relaxed cursor-pointer">
                      <input type="checkbox" data-testid="contact-privacy-checkbox" checked={form.privacy} onChange={set("privacy")} className="mt-0.5 accent-sage" />
                      <span>
                        {c.form.privacyPre}
                        <Link to={l("/privacy-policy")} className="underline hover:text-sage">{c.form.privacyLink}</Link>
                        {c.form.privacyPost}
                      </span>
                    </label>
                    {errors.privacy && <p className="mt-1.5 text-xs text-red-600">{errors.privacy}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-form-submit-button"
                      className="inline-flex items-center rounded-full bg-terra px-8 py-4 text-sm font-semibold text-cream transition-[background-color,transform,opacity] duration-300 hover:bg-terra-dark hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
                    >
                      {sending ? c.form.sending : c.form.submit}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </Reveal>
      </section>
    </>
  );
}
